import { authenticationService } from '../../services';
import { setAccessDeniedContext } from '../../atoms/accessDeniedAtom';
import { classifyDenial } from '../access/classifyDenial';
import type { AccessRequestContext } from '../access/accessDeniedTypes';
import { resolveFallbackRoute, resolveResourceLabel } from '../access/routeAccessLabels';
import { parseApiError } from './parseApiError';

type ResponseLike = {
    text: () => Promise<string>;
    ok: boolean;
    status: number;
    statusText: string;
    url?: string;
};

export function handleResponse(
    response: ResponseLike,
    accessContext?: AccessRequestContext
) {
    const requestUrl = accessContext?.requestUrl ?? response.url;
    const context: AccessRequestContext = {
        ...accessContext,
        requestUrl,
        pathname: accessContext?.pathname ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
    };

    return response.text().then((text: string) => {
        let data: unknown = null;
        let hasData = false;
        try {
            data = text && JSON.parse(text);
            hasData = true;
        } catch {
            // non-JSON body
        }

        if (!response.ok) {
            const apiError = hasData ? parseApiError(data) : {};

            if (response.status === 401) {
                authenticationService.logout();
            }

            const isPermissionOrValidationError =
                response.status === 403 ||
                (response.status === 400 && apiError.code);

            if (isPermissionOrValidationError) {
                const classification = classifyDenial({
                    code: apiError.code,
                    message: apiError.message,
                    httpStatus: response.status,
                    context,
                });

                if (classification.redirectToAccessDenied) {
                    const pathname = context.pathname ?? "";
                    setAccessDeniedContext({
                        source: classification.source,
                        resourceSubtype: classification.resourceSubtype,
                        attemptedPath: pathname + (typeof window !== "undefined" ? window.location.search : ""),
                        resourceLabel: resolveResourceLabel(pathname),
                        code: apiError.code,
                        message: apiError.message,
                        fallbackRoute: resolveFallbackRoute(pathname),
                        callerHint: context.callerHint,
                    });
                }
            }

            if (response.status === 400) {
                if (data && typeof data === "object" && data !== null && "message" in data) {
                    const msg = (data as { message: unknown }).message;
                    if (typeof msg === "string") return Promise.reject(data);
                }
                if (apiError.message) return Promise.reject(apiError);
                return Promise.reject(hasData ? data : text);
            }

            if (apiError.message || apiError.code) {
                return Promise.reject(apiError);
            }

            const error =
                (data && typeof data === "object" && data !== null && "message" in data
                    ? (data as { message: string }).message
                    : undefined) || response.statusText;
            return Promise.reject(error);
        }

        return hasData ? data : text;
    });
}
